import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ClienteDataService,
  ClienteService,
  EstabelecimentoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { IEstabelecimentoClienteEntity } from '@nomoh-monorepo/shared-types';
import { MaskUtils } from '@nomoh-monorepo/shared-util-tools';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ClienteViewModalComponent } from './modals/cliente-view-modal/cliente-view-modal.component';

/**
 * Componente de listagem de clientes do Estabelecimento (design reformulado da agenda)
 */
@Component({
  selector: 'lib-clientes',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ConfirmDialogModule,
    TooltipModule,
    TableModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    FormsModule,
    ClienteViewModalComponent,
    TranslateModule,
  ],
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.scss'],
  providers: [ConfirmationService],
})
export class ClientesComponent {
  private clienteService = inject(ClienteService);
  private clienteDataService = inject(ClienteDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private nichoTermosService = inject(NichoTermosService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);

  // Signals
  $termos = this.nichoTermosService.$termos;
  $clientes = this.clienteDataService.$clientes;
  $total = this.clienteDataService.$total;
  $loading = this.clienteDataService.$loading;
  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;

  modalViewAberto = signal(false);
  modalEditAberto = signal(false);
  clienteSelecionado = signal<IEstabelecimentoClienteEntity | null>(null);

  // Controle de Paginação e Filtro
  pesquisa = signal('');
  first = signal(0);
  rows = signal(10);

  skeletonCards = [1, 2, 3, 4, 5, 6];
  private avatarPalette = [
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#F97316',
    '#EA580C',
  ];

  private searchTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    // Monitorar mudança de estabelecimento para carregar clientes
    effect(() => {
      const est = this.$estabelecimentoAtual();

      if (est?.id) {
        untracked(() => {
          this.first.set(0);
          this.carregarClientes();
        });
      }
    });
  }

  async carregarClientes() {
    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) return;

    try {
      const page = Math.floor(this.first() / this.rows()) + 1;
      await this.clienteService.findAllPaginado(
        estabelecimentoId,
        page,
        this.rows(),
        this.pesquisa()
      );
    } catch {
      // Trata erro graciosamente
    }
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first.set(event.first ?? 0);
    this.rows.set(event.rows ?? 10);
    this.carregarClientes();
  }

  onPesquisaInput(val: string): void {
    this.pesquisa.set(val);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.first.set(0);
      this.carregarClientes();
    }, 350);
  }

  buscarComFiltro(): void {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.first.set(0);
    this.carregarClientes();
  }

  limparPesquisa(): void {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.pesquisa.set('');
    this.first.set(0);
    this.carregarClientes();
  }

  abrirModalNovo() {
    this.clienteSelecionado.set({} as IEstabelecimentoClienteEntity);
    this.modalEditAberto.set(true);
  }

  abrirModalView(cliente: IEstabelecimentoClienteEntity | null = null) {
    this.clienteSelecionado.set(cliente);
    this.modalViewAberto.set(true);
  }

  abrirModalEdit(cliente: IEstabelecimentoClienteEntity, event?: Event) {
    event?.stopPropagation();
    this.clienteSelecionado.set(cliente);
    this.modalEditAberto.set(true);
  }

  fecharModal() {
    this.modalViewAberto.set(false);
    this.modalEditAberto.set(false);
    this.clienteSelecionado.set(null);
  }

  async onSalvar() {
    this.fecharModal();
    await this.carregarClientes();
  }

  confirmarExclusao(cliente: IEstabelecimentoClienteEntity, event: Event) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translateService.instant('BARDO_CLIENTES.CONFIRMAR_EXCLUSAO_MSG', {
        termo: this.$termos().cliente.singular.toLowerCase(),
        nome: cliente.nome,
      }),
      header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
      rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.excluirCliente(cliente.id as string),
    });
  }

  async excluirCliente(id: string) {
    try {
      await this.clienteService.deletar(id);
      await this.carregarClientes();
    } catch {
      // Silent catch
    }
  }

  getIniciais(nome: string): string {
    if (!nome) return '';
    return nome
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  formatarTelefone(telefone: string | undefined | null): string {
    if (!telefone) return '';
    return MaskUtils.formatarTelefone(telefone);
  }

  getAvatarColor(clienteId: string | undefined): string {
    if (!clienteId) return this.avatarPalette[0];
    let sum = 0;
    for (let i = 0; i < clienteId.length; i++) {
      sum += clienteId.charCodeAt(i);
    }
    const index = sum % this.avatarPalette.length;
    return this.avatarPalette[index];
  }
}

